import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ObjectType } from 'api/pushes';
import { Button } from 'components/atoms/button';
import { OzoneDrawer as Drawer } from 'components/atoms/drawer';
import {
  BackgroundColor,
  Height,
  InputBorder,
  InputFocus,
} from 'components/atoms/input/input';
import { Filter } from 'components/molecules/filters/types';
import { Fields, Select } from 'components/organisms/tasks/criteria/components';
import { AdvancedFilterComponent } from 'components/organisms/tasks/filter-box/advanced';
import { Box } from 'components/organisms/tasks/filter-box/components';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { useDynamicConditionFields } from 'data/templates/projects/hooks/use-project-dynamic-template';
import { useTranslation } from 'hooks/use-translation';

const FilterComponent = styled(AdvancedFilterComponent)`
  margin-top: 20px;
`;

const ContentHeader = styled.p`
  margin-bottom: '20px';
`;

/**
 * Following styled component is referred from the one used for widget
 * TODO: need to fix the styling at original component rather than at consumer end
 */
const FilterWrapper = styled(Box)`
  ${Fields} {
    ${BackgroundColor};
    ${InputBorder};
    ${Height};
    padding-left: 0;

    &.ant-select-open {
      ${InputFocus};

      .ant-select-selection-item {
        color: var(--gray-100);
      }
    }

    .ant-select-selector {
      ${Height};
      display: flex;
      align-items: center;
      padding-left: var(--spacing-05);
    }
  }

  ${Select} {
    padding-left: 0;

    .ant-select-selector {
      padding-left: var(--spacing-05);
    }
  }
`;

const Footer: FC<{
  onClose: VoidFunction;
  onDone: VoidFunction;
}> = ({ onClose, onDone }) => {
  return (
    <>
      <Button onClick={onClose}>{useTranslation('cancel')}</Button>
      <Button type="primary" onClick={onDone}>
        {useTranslation('done')}
      </Button>
    </>
  );
};

export const DynamicConditionsDrawer: FC<{
  title?: string | React.ReactNode;
  type?: string;
  filter: Filter;
  onSubmit: (_filter, shiftDaysInput) => void;
  visible: boolean;
  onClose: VoidFunction;
}> = ({ title, type, filter: _filter, onSubmit, visible, onClose }) => {
  const fields = useDynamicConditionFields();
  const [filter, setFilter] = useState(_filter);
  const userInputRef = useRef(null as any);

  useEffect(() => {
    setFilter(_filter);
  }, [_filter]);

  const onDone = () => {
    const validCriteria = filter.criteria.filter((_criteria) =>
      FieldFilter.isValid(_criteria),
    );

    onSubmit(
      { ...filter, criteria: validCriteria },
      userInputRef.current?.input.value || 0,
    );
  };

  const headerContentType = () => {
    if (type === ObjectType.ProjectPhase) {
      return 'templates.dynamic.conditions.drawer.content.header.phase';
    }
    if (type === ObjectType.Task) {
      return 'templates.dynamic.conditions.drawer.content.header.task';
    }
    if (type === ObjectType.ProjectSpace) {
      return 'templates.dynamic.conditions.drawer.content.header.space';
    }

    return 'templates.dynamic.conditions.drawer.content.header.space_tab';
  };

  return (
    <Drawer
      title={title}
      width={880}
      visible={visible}
      onClose={onClose}
      footer={<Footer onClose={onClose} onDone={onDone} />}
    >
      <ContentHeader>
        <b>
          <FormattedMessage id={headerContentType()} />
        </b>
      </ContentHeader>
      <FilterWrapper>
        <FilterComponent fields={fields} filter={filter} onChange={setFilter} />
      </FilterWrapper>
    </Drawer>
  );
};
